import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Skeleton,
  Grid,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  MdDelete,
  MdEdit,
  MdAdd,
  MdSearch,
  MdCampaign,
  MdImage,
  MdGridView,
  MdViewList,
  MdRefresh,
  MdClear,
  MdCollections,
} from "react-icons/md";
import toast from "react-hot-toast";
import {
  getAllBanners,
  createBanner,
  updateBanner,
  deleteBanner,
} from "../apis/bannerApi";
import { brand } from "../theme";

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: `${brand.brass}66`,
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || brand.brass}18`,
        color: accent || brand.ink950,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: "1.8rem",
          color: brand.ink950,
          lineHeight: 1.1,
        }}
      >
        {count}
      </Typography>
      <Typography sx={{ color: brand.slate, fontSize: "0.82rem", fontWeight: 600 }} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const Banner = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: "", image: null });
  const [selectedId, setSelectedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

  const loadBanners = async () => {
    setLoading(true);
    try {
      const res = await getAllBanners();
      setBanners(Array.isArray(res) ? res : []);
    } catch {
      toast.error("Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBanners();
  }, []);

  const stats = useMemo(() => {
    let withImage = 0;
    banners.forEach((b) => {
      if (b.image && b.image.trim() !== "") withImage += 1;
    });

    return {
      total: banners.length,
      withImage,
      coverage: banners.length ? Math.round((withImage / banners.length) * 100) : 0,
    };
  }, [banners]);

  const filteredBanners = useMemo(() => {
    if (!search.trim()) return banners;
    const q = search.toLowerCase();
    return banners.filter((b) => (b.title || "").toLowerCase().includes(q));
  }, [banners, search]);

  const paginatedBanners = useMemo(() => {
    return filteredBanners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredBanners, page, rowsPerPage]);

  /* ------------------ HANDLERS ------------------ */

  const handleOpen = (banner = null) => {
    if (banner) {
      setForm({ title: banner.title || "", image: null });
      setEditing(true);
      setSelectedId(banner.id);
    } else {
      setForm({ title: "", image: null });
      setEditing(false);
      setSelectedId(null);
    }
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setForm({ title: "", image: null });
    setSelectedId(null);
    setEditing(false);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files && files[0]) {
      setForm((prev) => ({ ...prev, image: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error("Banner title is required");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", form.title);
      if (form.image) formData.append("image", form.image);

      if (editing && selectedId) {
        await updateBanner(selectedId, formData);
        toast.success("Banner updated successfully");
      } else {
        await createBanner(formData);
        toast.success("Banner created successfully");
      }

      handleClose();
      loadBanners();
    } catch {
      toast.error("Error saving banner");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteBanner(confirmDelete.id);
      toast.success("Banner deleted successfully");
      loadBanners();
    } catch {
      toast.error("Failed to delete banner");
    } finally {
      setConfirmDelete({ open: false, id: null });
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const formatImageSrc = (imgPath) => {
    if (!imgPath) return null;
    if (imgPath.startsWith("http")) return imgPath;
    return `https://skishore.in/api/public/${imgPath}`;
  };

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h3"
              sx={{
                color: brand.ink950,
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Promotional Banners
            </Typography>
            <Chip
              label={`${banners.length} Active Assets`}
              size="small"
              sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700 }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            Manage homepage banners, marketing campaign sliders, and promotional hero graphics
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload banner assets">
            <IconButton
              onClick={loadBanners}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleOpen()}
            startIcon={<MdAdd size={20} />}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              boxShadow: "0 8px 20px -6px rgba(201,162,39,0.5)",
            }}
          >
            Add Banner
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards Grid ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard icon={<MdCampaign size={24} />} label="Total Banners" count={stats.total} accent="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<MdImage size={24} />}
            label="Visual Cover Coverage"
            count={`${stats.coverage}%`}
            accent={brand.brass}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<MdCollections size={24} />}
            label="Marketing Assets"
            count="Active"
            accent="#10B981"
          />
        </Grid>
      </Grid>

      {/* ── Filter Controls Bar ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          {/* Search Bar */}
          <TextField
            placeholder="Search by banner title..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              width: { xs: "100%", sm: 340 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: `${brand.slateLight}15`,
                fontSize: "0.85rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch color={brand.slate} size={20} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <MdClear size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* View Switcher */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
            sx={{
              bgcolor: `${brand.slateLight}20`,
              p: 0.4,
              borderRadius: "12px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px",
                px: 1.5,
                py: 0.5,
                color: brand.slate,
                "&.Mui-selected": {
                  bgcolor: brand.paper,
                  color: brand.ink950,
                  boxShadow: "0 2px 8px rgba(11,10,61,0.08)",
                },
              },
            }}
          >
            <ToggleButton value="table">
              <MdViewList size={18} />
            </ToggleButton>
            <ToggleButton value="grid">
              <MdGridView size={18} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* ── Table vs Grid View ─────────────────────────────────────────── */}
      {viewMode === "table" ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            border: `1px solid ${brand.slateLight}40`,
            bgcolor: brand.paper,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Banner Preview</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Banner Title</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Created Timestamp</TableCell>
                <TableCell align="right" sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedBanners.length > 0 ? (
                paginatedBanners.map((banner, index) => {
                  const imgUrl = formatImageSrc(banner.image);
                  return (
                    <TableRow
                      key={banner.id || index}
                      sx={{ "&:hover": { bgcolor: `${brand.brassSoft}60` } }}
                    >
                      <TableCell
                        sx={{
                          color: brand.slate,
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontSize: "0.82rem",
                          fontWeight: 600,
                        }}
                      >
                        {String(page * rowsPerPage + index + 1).padStart(2, "0")}
                      </TableCell>
                      <TableCell>
                        {imgUrl ? (
                          <Box
                            component="img"
                            src={imgUrl}
                            alt={banner.title}
                            sx={{
                              width: 80,
                              height: 45,
                              borderRadius: "10px",
                              objectFit: "cover",
                              border: `1px solid ${brand.slateLight}50`,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 80,
                              height: 45,
                              borderRadius: "10px",
                              bgcolor: `${brand.slateLight}30`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: brand.slate,
                            }}
                          >
                            <MdImage size={22} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", color: brand.ink950 }}>
                        {banner.title || "Untitled Banner"}
                      </TableCell>
                      <TableCell sx={{ color: brand.slate, fontSize: "0.82rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                        {banner.created_at || "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={0.8} justifyContent="flex-end">
                          <MuiTooltip title="Edit Banner">
                            <IconButton
                              size="small"
                              onClick={() => handleOpen(banner)}
                              sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                            >
                              <MdEdit size={16} />
                            </IconButton>
                          </MuiTooltip>
                          <MuiTooltip title="Delete Banner">
                            <IconButton
                              size="small"
                              onClick={() => setConfirmDelete({ open: true, id: banner.id })}
                              sx={{ bgcolor: `${brand.danger}15`, color: brand.danger }}
                            >
                              <MdDelete size={16} />
                            </IconButton>
                          </MuiTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <MdCampaign size={40} color={brand.slateLight} />
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: brand.ink950,
                        mt: 1,
                      }}
                    >
                      No promotional banners found
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
                      {search
                        ? "Try a different search title."
                        : "Click 'Add Banner' to upload your first promotional graphic."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredBanners.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3} mb={3}>
          {paginatedBanners.map((banner) => {
            const imgUrl = formatImageSrc(banner.image);
            return (
              <Grid item xs={12} sm={6} md={4} key={banner.id}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "22px",
                    bgcolor: brand.paper,
                    border: `1px solid ${brand.slateLight}40`,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 18px 40px -15px rgba(11,10,61,0.12)",
                      borderColor: `${brand.brass}66`,
                    },
                  }}
                >
                  <Box position="relative" height={160} bgcolor={`${brand.ink950}10`}>
                    {imgUrl ? (
                      <Box
                        component="img"
                        src={imgUrl}
                        alt={banner.title}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%" color={brand.slate}>
                        <MdImage size={40} />
                      </Box>
                    )}
                  </Box>

                  <Box p={2.5}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: brand.ink950 }} noWrap>
                      {banner.title || "Untitled Banner"}
                    </Typography>

                    <Typography sx={{ fontSize: "0.78rem", color: brand.slate, mt: 0.5, fontFamily: '"IBM Plex Mono", monospace' }}>
                      Created: {banner.created_at || "Date N/A"}
                    </Typography>
                  </Box>

                  <Box display="flex" gap={1} justifyContent="flex-end" p={2} pt={0}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpen(banner)}
                      startIcon={<MdEdit />}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setConfirmDelete({ open: true, id: banner.id })}
                      startIcon={<MdDelete />}
                    >
                      Delete
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Dialog: Add / Edit Banner ─────────────────────────────────── */}
      <Dialog
        open={openDialog}
        onClose={handleClose}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 480 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          {editing ? "Edit Banner Graphic" : "Upload New Banner"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Banner Title *"
              name="title"
              value={form.title}
              onChange={handleChange}
              fullWidth
            />
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: brand.slate, mb: 1 }}>
                Banner Graphic Image *
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                {form.image && (
                  <Box
                    component="img"
                    src={URL.createObjectURL(form.image)}
                    alt="Preview"
                    sx={{ width: 110, height: 60, borderRadius: "10px", objectFit: "cover" }}
                  />
                )}
                <Button variant="outlined" component="label" sx={{ borderRadius: "10px", textTransform: "none" }}>
                  Select Image File
                  <input type="file" hidden name="image" accept="image/*" onChange={handleChange} />
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="secondary"
            disabled={saving}
            sx={{ fontWeight: 700, borderRadius: "10px", px: 3 }}
          >
            {saving ? "Saving..." : editing ? "Update Banner" : "Create Banner"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirm Delete ─────────────────────────────────────── */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, id: null })}
        PaperProps={{ sx: { borderRadius: "20px", p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700 }}>
          Delete Banner Asset?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: brand.slate, fontSize: "0.9rem" }}>
            Are you sure you want to delete this promotional banner asset? This operation cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete({ open: false, id: null })} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" sx={{ fontWeight: 700 }}>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Banner;
